import { GrElement, TOKENS_CSS } from "../core/base";

/**
 * <gr-toggle topic="matrix/power" label="Power"></gr-toggle>
 *
 * Publishes true/false on its topic (the serial codec turns that into
 * `POWER:1` / `POWER:0`). Reflects state published by others — device
 * acks or another control on the same topic.
 */
export class GrToggle extends GrElement {
  private on = false;

  connectedCallback(): void {
    const source = this.publisherSource("gr-toggle");
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: var(--_radius);
          background: var(--_bg);
          border: 1px solid var(--_border);
        }
        .switch {
          position: relative;
          width: 44px;
          height: 24px;
          border-radius: 999px;
          border: 1px solid var(--_border);
          background: rgba(255, 255, 255, 0.08);
          cursor: pointer;
          transition: 0.2s;
          flex-shrink: 0;
        }
        .knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: var(--_muted);
          transition: 0.2s;
        }
        :host([data-on]) .switch { background: color-mix(in srgb, var(--_accent) 35%, transparent); border-color: var(--_accent); }
        :host([data-on]) .knob { left: 22px; background: var(--_accent); }
      </style>
      <span class="label"></span>
      <div class="switch" role="switch" tabindex="0"><div class="knob"></div></div>
    `;
    root.querySelector(".label")!.textContent = this.attr("label", this.attr("topic"));
    const flip = () => {
      this.on = !this.on;
      this.reflect();
      this.bus.publish(this.attr("topic"), this.on, { src: source });
    };
    const switchEl = root.querySelector<HTMLElement>(".switch")!;
    switchEl.addEventListener("click", flip);
    switchEl.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        flip();
      }
    });
    this.track(
      this.bus.subscribe(this.attr("topic"), (msg) => {
        if (msg.src === source) return;
        this.on = msg.payload === true || msg.payload === 1 || msg.payload === "1";
        this.reflect();
      }),
    );
  }

  private reflect(): void {
    this.toggleAttribute("data-on", this.on);
    this.shadowRoot?.querySelector(".switch")?.setAttribute("aria-checked", String(this.on));
  }
}

customElements.define("gr-toggle", GrToggle);
